import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { agenciesApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Building, MapPin, Phone, Mail, Globe, ArrowRight, Home } from 'lucide-react';

export const AgenciesPage = () => {
  const { language } = useLanguage();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: language === 'fr' ? 'Agences immobilieres en Algerie | Beytam' : 'الوكالات العقارية في الجزائر | بيتم',
    description: language === 'fr' ? 'Trouvez les meilleures agences immobilieres en Algerie. Consultez leurs annonces et contactez-les.' : 'اعثر على أفضل الوكالات العقارية في الجزائر.',
    ogTitle: language === 'fr' ? 'Agences immobilieres - Beytam' : 'الوكالات العقارية - بيتم',
    canonical: window.location.href,
  });

  useEffect(() => {
    agenciesApi.list().then(res => setAgencies(res.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <Skeleton className="h-10 w-72 mb-4" />
          <Skeleton className="h-5 w-96 mb-10" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agencies-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-blue-900 to-blue-800 text-white py-14">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <Building className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="font-manrope text-3xl md:text-4xl font-bold mb-3">
            {language === 'fr' ? 'Agences immobilieres' : 'الوكالات العقارية'}
          </h1>
          <p className="text-blue-200 max-w-lg mx-auto">
            {language === 'fr'
              ? 'Trouvez les agences professionnelles pour votre projet immobilier'
              : 'اعثر على الوكالات المهنية لمشروعك العقاري'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        {agencies.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-16 text-center">
              <Building className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {language === 'fr' ? 'Aucune agence inscrite' : 'لا توجد وكالات مسجلة'}
              </h3>
              <p className="text-slate-500 text-sm">
                {language === 'fr'
                  ? 'Les agences immobilieres peuvent creer leur profil en s\'inscrivant en tant qu\'agence.'
                  : 'يمكن للوكالات العقارية إنشاء ملفها الشخصي بالتسجيل كوكالة.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agencies.map(agency => (
              <Link key={agency.id} to={`/agencies/${agency.id}`} data-testid={`agency-card-${agency.id}`}>
                <Card className="border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        {agency.logo ? (
                          <img src={agency.logo} alt={agency.agency_name} className="w-14 h-14 rounded-xl object-cover" />
                        ) : (
                          <Building className="w-7 h-7 text-blue-900" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 text-lg truncate">{agency.agency_name}</h3>
                        {agency.city && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3.5 h-3.5" /> {agency.city}
                          </p>
                        )}
                      </div>
                    </div>
                    {agency.description && (
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                        {language === 'ar' && agency.description_ar ? agency.description_ar : agency.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <Badge className="bg-blue-50 text-blue-800 border-0 gap-1">
                        <Home className="w-3 h-3" /> {agency.listing_count || 0} {language === 'fr' ? 'annonces' : 'إعلانات'}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
